import { useState } from "react";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import { api, errMsg, fileUrl, fmtDate, useFetch } from "@/lib/api";
import { divisionName } from "@/lib/content";

const RequestRow = ({ r, onDone }) => {
  const [consent, setConsent] = useState(false);
  const [note, setNote] = useState("");
  const review = async (action) => {
    try {
      await api.post(`/admin/profile-requests/${r.id}/review`, { action, guardian_consent: consent, note });
      toast.success(action === "approve" ? "Approved — recruiting card is live" : "Request declined");
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const tone = { pending: "border-gold text-gold", approved: "border-emerald-500 text-emerald-400", declined: "border-red-500 text-red-400" }[r.status];
  return (
    <div className="card-navy p-5 grid gap-5 md:grid-cols-[140px_1fr]" data-testid={`profile-request-${r.id}`}>
      <div className="aspect-[3/4] bg-pitch overflow-hidden">{r.photo_url && <img src={fileUrl(r.photo_url)} alt={r.name} className="h-full w-full object-cover" />}</div>
      <div className="min-w-0 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <p className="font-display text-2xl font-black uppercase italic">{r.name}</p>
          <span className={`border px-2 py-0.5 font-mono text-[10px] uppercase ${tone}`}>{r.status}</span>
          <span className="font-mono text-[10px] uppercase text-slate-400">{divisionName(r.division) || "No division"} • {fmtDate(r.updated_at || r.created_at)}</span>
        </div>
        <p className="text-sm text-slate-300">{[r.position, r.jersey && `#${r.jersey}`, r.home_org].filter(Boolean).join(" • ") || "No details"}</p>
        {r.accomplishments && <p className="text-sm text-slate-300 whitespace-pre-line">{r.accomplishments}</p>}
        {r.bio && <p className="text-sm text-slate-400">{r.bio}</p>}
        {r.media_url && <a href={r.media_url} target="_blank" rel="noreferrer" className="text-sm text-gold underline">Highlight video</a>}
        {r.status === "pending" ? (
          <div className="space-y-3 border-t border-line pt-4">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="h-5 w-5 accent-[#FFCB05]" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid={`profile-request-consent-${r.id}`} />Parent/guardian permission is on file</label>
            <input className="field !py-2" placeholder="Note to player (optional, sent by email)" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`profile-request-note-${r.id}`} />
            <div className="flex gap-3">
              <button onClick={() => review("approve")} disabled={!consent} className="btn-gold !py-2 !text-sm" data-testid={`profile-request-approve-${r.id}`}><Check className="h-4 w-4" /> Approve & Publish</button>
              <button onClick={() => review("decline")} className="btn-line !py-2 !text-sm" data-testid={`profile-request-decline-${r.id}`}><X className="h-4 w-4" /> Decline</button>
            </div>
          </div>
        ) : r.note ? <p className="text-xs text-slate-500">Note: {r.note}</p> : null}
      </div>
    </div>
  );
};

export const ProfileRequestsPanel = () => {
  const { data, loading, reload } = useFetch("/admin/profile-requests");
  if (loading) return <p className="text-slate-400">Loading…</p>;
  return (
    <div className="space-y-4" data-testid="profile-requests-panel">
      <h2 className="font-display text-3xl font-black uppercase italic">Recruiting Card Requests</h2>
      <p className="text-sm text-slate-400">Approved players submit their photo and details here. Approving publishes (or updates) their card on The Coast's Next Up — you can edit or feature it later in the Athletes tab.</p>
      {!data?.length ? <p className="text-slate-400" data-testid="profile-requests-empty">No requests yet.</p> : data.map((r) => <RequestRow key={r.id} r={r} onDone={reload} />)}
    </div>
  );
};
